import {Suspense, useEffect, useMemo} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {Navigate} from 'react-router';
import {useSearchParams} from 'react-router-dom';
import clsx from 'clsx';
import {PermissionTypeKr, RoleType} from '@di-shared/api/di/type';
import {emitEventLog, emitPageView} from '@di-shared/features/analytics';
import Header from '@di-shared/features/common/Header';
import HeaderCloseButton from '@di-shared/features/common/HeaderCloseButton';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useFirebaseStore from '@di-shared/features/environment/firebaseStore';
import HomeTitleHeader from '@di-shared/features/home/HomeTitleHeader';
import ConsentBannerKr from '@di-shared/features/home/kr/ConsentBanner.kr';
import ConsentBannerLoadingKr from '@di-shared/features/home/kr/ConsentBannerLoading.kr';
import BenefitContentsKr from '@di-shared/features/home/kr/benefit/BenefitContents.kr';
import DrivingContentsKr from '@di-shared/features/home/kr/driving/DrivingContents.kr';
import FinanceBetaContents from '@di-shared/features/home/kr/finance/FinanceBetaContents';
import FinanceContents from '@di-shared/features/home/kr/finance/FinanceContents';
import useAvailableComponentKr from '@di-shared/features/home/kr/useAvailableComponent.kr';
import HomeSestExchangeHistoryKr from '@di-shared/features/home/kr/vehicle/HomeSestExchangeHistory.kr';
import VehicleContentsKr from '@di-shared/features/home/kr/vehicle/VehicleContents.kr';
import {isEventDuration} from '@di-shared/pages/[vin]/capital/event1';
import {ROUTE_PATHS} from '@di-shared/routes';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {WebviewUtils} from '@shared/webviewUtils';

const TAB = 'tab';

export enum TabType {
  Driving = 'driving',
  Vehicle = 'vehicle',
  Finance = 'finance',
  FinanceBeta = 'finance_beta',
  Benefit = 'benefit',
}

export interface HomeContentsKrProps {
  vin: string;
  puid: string;
  ccid: string;
}

function HomeContentsKr({vin, puid, ccid}: HomeContentsKrProps) {
  // 순환참조(routes/index.tsx → HomePage → HomeContentsKr → routes/index.tsx)를 피하기 위해 컴포넌트 내부에 정의.
  // ROUTE_PATHS는 정적 상수이므로 useMemo의 deps는 빈 배열로 안전.
  const TAB_PAGE_ID = useMemo<Record<TabType, string>>(() => ({
    [TabType.Driving]: ROUTE_PATHS.HOME_DRIVING_TAB.id,
    [TabType.Vehicle]: ROUTE_PATHS.HOME_VEHICLE_TAB.id,
    [TabType.Finance]: ROUTE_PATHS.HOME_FINANCE_TAB.id,
    [TabType.FinanceBeta]: ROUTE_PATHS.HOME_FINANCE_TAB.id,
    [TabType.Benefit]: ROUTE_PATHS.HOME_BENEFIT_TAB.id,
  }), []);

  const {t} = useInferableTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {isCcsFullEnroll, isMainSupport, role, checkAvailableComponent} = useAvailableComponentKr({vin});
  const {checkCapitalVinWhitelist} = useFirebaseStore(({checkCapitalVinWhitelist}) => ({checkCapitalVinWhitelist}));

  const tabType = useMemo(() => {
    return searchParams.get(TAB) || TabType.Driving;
  }, [searchParams]);

  function changeTabType(tabType: TabType) {
    searchParams.set(TAB, tabType.toString());
    setSearchParams(searchParams, {replace: true});
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps -- TAB_PAGE_ID는 useMemo([])로 안정화된 정적 상수이므로 의존성에서 제외
  useEffect(() => {
    const pageId = TAB_PAGE_ID[tabType as TabType];
    if (pageId) {
      emitPageView(pageId);
    }
  }, [tabType]);

  /**
   * [2025-10-28:Jeyoung Lee] FIXME: 이벤트 종료 시 제거 EVENT2
   */
  async function openEventBottomSheet() {
    if (!(checkCapitalVinWhitelist(vin) || isEventDuration())) {
      return;
    }
    const isGoToEvent = await ccWebviewUtils.openBottomSheetWebViewUrl(ROUTE_PATHS.BOTTOM_SHEET_CAPITAL_EVENT_1_INTRO.getFullPath());
    if (isGoToEvent === false) {
      return;
    }
    if (isGoToEvent === 0) {
      searchParams.set('tab', 'finance');
      setSearchParams(searchParams, {replace: true});
      emitEventLog({
        event: 'di_driveinfo_interaction',
        data_action: 'bottom_sheet_popup',
        data_name: '자동차 보험 연결하Go 시원한 커피도 Get!_바로가기',
      });
      setTimeout(() => {
        // IOS 에서 바텀시트가 닫히는 근처 시점에 모달이 열려버리면, 그것도 같이 닫아버리는 이슈가 있음
        // 그래서 조금 늦게 열어서 바텀시트가 닫히고 열리게 함
        WebviewUtils.openNewWebView({
          url: window.location.origin + ROUTE_PATHS.CAPITAL_EVENT_1.getFullPath(vin),
          isModal: true,
          isNavigationBar: false,
          isToolBar: false,
        });
      }, 1000);
    } else {
      emitEventLog({
        event: 'di_driveinfo_interaction',
        data_action: 'bottom_sheet_popup',
        data_name: '자동차 보험 연결하Go 시원한 커피도 Get!_오늘 하루 보지 않기',
      });
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      localStorage.setItem('event1BlockedUntil', tomorrow.getTime().toString());
    }
  }

  /**
   * [2025-10-28:Jeyoung Lee] FIXME: 이벤트 종료 시 제거 EVENT2
   */
  useEffect(() => {
    if (!(checkCapitalVinWhitelist(vin) || isEventDuration())) {
      return;
    }
    const blockedUntil = localStorage.getItem('event1BlockedUntil');
    if (blockedUntil && Date.now() < parseInt(blockedUntil, 10)) {
      return;
    }

    if (blockedUntil && Date.now() >= parseInt(blockedUntil, 10)) {
      localStorage.removeItem('event1BlockedUntil');
    }

    openEventBottomSheet();
  }, []);

  if (!isCcsFullEnroll || !isMainSupport || role === RoleType.NON_CCS || role === RoleType.UNREGISTERED_SPEC) {
    return (
      <Navigate
        to={ROUTE_PATHS.DIAG.getFullPath(vin)}
        replace
      />
    );
  }

  return (
    <div className="wrap">
      <Header
        className="header_absolute"
        right={<HeaderCloseButton onClick={() => ccWebviewUtils.closeContent()} />}
      />
      <main className="content page_home">
        <ErrorBoundary fallback={<HomeTitleHeader.Error />}>
          <Suspense fallback={<HomeTitleHeader.Loading />}>
            <HomeTitleHeader vin={vin} />
          </Suspense>
        </ErrorBoundary>
        <ErrorBoundary fallback={<></>}>
          <Suspense fallback={<ConsentBannerLoadingKr />}>
            <ConsentBannerKr vin={vin} />
          </Suspense>
        </ErrorBoundary>
        <div
          className="tabbar_txt tabbar_sticky"
          role="tablist"
        >
          <div className="inner">
            <button
              type="button"
              className={clsx('tabbar_item', 'title_xsmall_b', {
                'is-selected': tabType === TabType.Driving,
              })}
              onClick={() => {
                emitEventLog({
                  event: 'di_tabs',
                  data_name: 'driving_records',
                });
                changeTabType(TabType.Driving);
              }}
              id={`${TabType.Driving}_tab`}
              role="tab"
              aria-selected={tabType === TabType.Driving}
              aria-controls={`${TabType.Driving}_tabpanel`}
              tabIndex={tabType === TabType.Driving ? 0 : -1}
            >
              <span className="txt">{t('T20049')}</span>
            </button>
            <button
              type="button"
              className={clsx('tabbar_item', 'title_xsmall_b', {
                'is-selected': tabType === TabType.Vehicle,
              })}
              onClick={() => {
                emitEventLog({
                  event: 'di_tabs',
                  data_name: 'vehicle_management',
                });
                changeTabType(TabType.Vehicle);
              }}
              id={`${TabType.Vehicle}_tab`}
              role="tab"
              aria-selected={tabType === TabType.Vehicle}
              aria-controls={`${TabType.Vehicle}_tabpanel`}
              tabIndex={tabType === TabType.Vehicle ? 0 : -1}
            >
              <span className="txt">{t('T20050')}</span>
            </button>
            {role === RoleType.PRIMARY_USER &&
              (checkAvailableComponent(PermissionTypeKr.FINANCIAL_MANAGEMENT) ||
                checkAvailableComponent(PermissionTypeKr.FINANCIAL_MANAGEMENT_3RD)) && (
                <button
                  type="button"
                  className={clsx('tabbar_item', 'title_xsmall_b', {
                    'is-selected': tabType === TabType.Finance,
                  })}
                  onClick={() => {
                    emitEventLog({
                      event: 'di_tabs',
                      data_name: 'vehicle_maintenance',
                    });
                    changeTabType(TabType.Finance);
                  }}
                  id={`${TabType.Finance}_tab`}
                  role="tab"
                  aria-selected={tabType === TabType.Finance}
                  aria-controls={`${TabType.Finance}_tabpanel`}
                  tabIndex={tabType === TabType.Finance ? 0 : -1}
                >
                  <span className="txt">{t('T40154')}</span>
                </button>
              )}
            {role === RoleType.PRIMARY_USER &&
              checkCapitalVinWhitelist(vin) && //차량유지 관련된 베타 페이지만 모아둔 운영용 베타 페이지. 화이트리스트 유지 필요.
              (checkAvailableComponent(PermissionTypeKr.FINANCIAL_MANAGEMENT) ||
                checkAvailableComponent(PermissionTypeKr.FINANCIAL_MANAGEMENT_3RD)) && (
                <button
                  type="button"
                  className={clsx('tabbar_item', 'title_xsmall_b', {
                    'is-selected': tabType === TabType.FinanceBeta,
                  })}
                  onClick={() => changeTabType(TabType.FinanceBeta)}
                  id={`${TabType.FinanceBeta}_tab`}
                  role="tab"
                  aria-selected={tabType === TabType.FinanceBeta}
                  aria-controls={`${TabType.FinanceBeta}_tabpanel`}
                  tabIndex={tabType === TabType.FinanceBeta ? 0 : -1}
                >
                  <span className="txt">{t('T40154')} (BETA)</span>
                </button>
              )}
            {role === RoleType.PRIMARY_USER &&
              (checkAvailableComponent(PermissionTypeKr.SEOUL) || checkAvailableComponent(PermissionTypeKr.UBI)) && (
                <button
                  type="button"
                  className={clsx('tabbar_item', 'title_xsmall_b', {
                    'is-selected': tabType === TabType.Benefit,
                  })}
                  onClick={() => {
                    emitEventLog({
                      event: 'di_tabs',
                      data_name: 'benefits',
                    });
                    changeTabType(TabType.Benefit);
                  }}
                  id={`${TabType.Benefit}_tab`}
                  role="tab"
                  aria-selected={tabType === TabType.Benefit}
                  aria-controls={`${TabType.Benefit}_tabpanel`}
                  tabIndex={tabType === TabType.Benefit ? 0 : -1}
                >
                  <span className="txt">{t('T20051')}</span>
                </button>
              )}
          </div>
        </div>
        {tabType === TabType.Driving && (
          <Suspense>
            <DrivingContentsKr vin={vin} />
          </Suspense>
        )}
        {tabType === TabType.Vehicle && (
          <>
            <Suspense>
              <VehicleContentsKr
                vin={vin}
                puid={puid}
                ccid={ccid}
              />
            </Suspense>
            <Suspense>
              <HomeSestExchangeHistoryKr vin={vin} />
            </Suspense>
          </>
        )}
        {tabType === TabType.Finance && <FinanceContents vin={vin} />}
        {tabType === TabType.FinanceBeta && checkCapitalVinWhitelist(vin) && <FinanceBetaContents vin={vin} />}
        {tabType === TabType.Benefit && (
          <Suspense>
            <BenefitContentsKr vin={vin} />
          </Suspense>
        )}
      </main>
    </div>
  );
}

export default HomeContentsKr;
